import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Users, Calendar, CalendarClock, Clock, TrendingUp, ChevronRight, Link2, Copy, Check, ExternalLink, Eye, UserCheck } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { companyApi } from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';

// Stats Card Component
const StatCard = ({ title, value, icon: Icon, description, variant = 'default' }) => (
  <Card className="card-hover">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-3xl font-heading font-semibold mt-1">{value}</p>
          {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          variant === 'warning' ? 'bg-amber-100' : 'bg-primary/10'
        }`}>
          <Icon className={`w-6 h-6 ${variant === 'warning' ? 'text-amber-600' : 'text-primary'}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function DashboardOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [companySlug, setCompanySlug] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, requestsRes, profileRes] = await Promise.all([
          companyApi.getStats(),
          companyApi.getSubscriptionRequests(),
          companyApi.getProfile()
        ]);
        setStats(statsRes.data);
        setRecentRequests(
          requestsRes.data
            .filter((r) => ['pending', 'advice_requested', 'awaiting_dates'].includes(r.status))
            .slice(0, 5)
        );
        
        // Use slug from profile if available, otherwise generate from company name
        let slug = profileRes.data.slug;
        if (!slug) {
          const companyName = profileRes.data.name || '';
          slug = companyName.toLowerCase()
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove accents
            .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with dashes
            .replace(/^-+|-+$/g, ''); // Remove leading/trailing dashes
        }
        setCompanySlug(slug);
      } catch (error) {
        toast.error('Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Use production URL if available, otherwise fall back to current origin
  const publicUrl = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
  const clientLoginUrl = companySlug ? `${publicUrl}/connexion/${companySlug}` : '';

  const copyClientLink = async () => {
    if (!clientLoginUrl) return;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(clientLoginUrl);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = clientLoginUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      setCopied(true);
      toast.success('Lien copié dans le presse-papier !');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Impossible de copier le lien. Copiez-le manuellement.');
    }
  };

  const openClientPreview = () => {
    if (clientLoginUrl) {
      window.open(clientLoginUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (isLoading) {
    return <div className="animate-pulse space-y-6">
      <div className="h-8 bg-muted rounded w-1/3" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-muted rounded-xl" />)}
      </div>
    </div>;
  }

  const maxCa = stats?.monthly_ca ? Math.max(...stats.monthly_ca.map(m => m.total_ca), 1) : 1;

  return (
    <div className="space-y-8 stagger-children">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Bienvenue, {user?.name}</h1>
        <p className="text-muted-foreground mt-1">Gérez votre activité et vos réservations</p>
      </div>

      {/* Pending client account validation banner — visible only when there's work to do.
          Without this, the user had to navigate to /company/clients to even
          notice that new clients had self-registered via widgets. */}
      {(stats?.pending_client_accounts || 0) > 0 && (
        <Card
          className="border-orange-300 bg-orange-50 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/company/clients?filter=pending')}
          data-testid="pending-client-accounts-banner"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                  <UserCheck className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-semibold text-orange-900">
                    {stats.pending_client_accounts === 1
                      ? '1 nouveau compte client à valider'
                      : `${stats.pending_client_accounts} nouveaux comptes clients à valider`}
                  </p>
                  <p className="text-xs text-orange-700 mt-0.5">
                    Vos clients se sont inscrits via vos widgets et attendent votre approbation pour accéder à leur espace.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="default"
                className="bg-orange-600 hover:bg-orange-700 shrink-0"
                data-testid="goto-pending-clients-btn"
              >
                Valider <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Clients" value={stats?.total_clients || 0} icon={Users} description="Total des clients" />
        <StatCard title="Créneaux réservés" value={stats?.upcoming_bookings || 0} icon={Calendar} description="À venir" />
        <StatCard title="Créneaux disponibles" value={stats?.available_slots || 0} icon={CalendarClock} description="Non réservés" />
        <StatCard 
          title="Demandes en attente" 
          value={(stats?.pending_requests || 0) + (stats?.pending_punctual_bookings || 0)} 
          icon={Clock}
          variant={(stats?.pending_requests || 0) + (stats?.pending_punctual_bookings || 0) > 0 ? 'warning' : 'default'}
          description="À traiter"
        />
      </div>

      {/* Client Login Link Card */}
      {companySlug && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Link2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-sm">Lien de connexion pour vos clients</p>
                  <p className="text-xs text-muted-foreground">Partagez ce lien avec vos clients pour qu'ils accèdent à leur espace client</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                <code className="text-xs bg-background px-3 py-2 rounded-lg border truncate block sm:max-w-xs">
                  {clientLoginUrl}
                </code>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={copyClientLink}
                    className="flex-1 sm:flex-none"
                    data-testid="copy-client-link-btn"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    <span className="ml-2">{copied ? 'Copié !' : 'Copier'}</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={openClientPreview}
                    className="flex-1 sm:flex-none"
                    data-testid="preview-client-space-btn"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="ml-2">Voir l'espace</span>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Chiffre d'affaires {new Date().getFullYear()}
              </CardTitle>
              <CardDescription>Évolution mensuelle du CA généré</CardDescription>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-primary">{(stats?.total_ca_year || 0).toFixed(2)}€</p>
              <p className="text-sm text-muted-foreground">CA total annuel</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-sm text-blue-700">Part client (50%)</p>
              <p className="text-2xl font-bold text-blue-800">{(stats?.client_part_year || 0).toFixed(2)}€</p>
            </div>
            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
              <p className="text-sm text-green-700">Part URSSAF (50%)</p>
              <p className="text-2xl font-bold text-green-800">{(stats?.urssaf_part_year || 0).toFixed(2)}€</p>
            </div>
          </div>

          <div className="h-48 flex items-end justify-between gap-2">
            {stats?.monthly_ca?.map((month) => {
              const heightPct = maxCa > 0 ? (month.total_ca / maxCa) * 100 : 0;
              return (
                <div key={month.month} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex flex-col justify-end h-36 relative group">
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-popover border rounded-lg p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap">
                      <p className="text-xs font-medium">{month.total_ca.toFixed(2)}€</p>
                      <p className="text-xs text-muted-foreground">Client: {month.client_part.toFixed(2)}€</p>
                      <p className="text-xs text-muted-foreground">URSSAF: {month.urssaf_part.toFixed(2)}€</p>
                    </div>
                    <div 
                      className="w-full rounded-t-md overflow-hidden transition-all hover:opacity-80"
                      style={{ height: `${heightPct}%`, minHeight: month.total_ca > 0 ? '4px' : '0' }}
                    >
                      <div className="w-full h-1/2 bg-blue-400" title="Part client" />
                      <div className="w-full h-1/2 bg-green-400" title="Part URSSAF" />
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{month.month_name}</span>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-400 rounded" />
              <span className="text-muted-foreground">Part client</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-400 rounded" />
              <span className="text-muted-foreground">Part URSSAF</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {recentRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Demandes en attente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentRequests.map((req) => (
              <div key={req.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                <div>
                  <p className="font-medium">{req.client_name}</p>
                  <p className="text-sm text-muted-foreground">{req.client_email}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium">{(req.estimated_monthly || req.estimated_price)?.toFixed(2)}€/mois</p>
                  <p className="text-sm text-muted-foreground">{req.passages_per_year || req.passages_per_month} passages/an</p>
                </div>
              </div>
            ))}
            <NavLink to="/company/requests">
              <Button variant="outline" className="w-full rounded-full">
                Voir toutes les demandes
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </NavLink>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
